/* swt-tech-wars.js · Homepage V2 · SWT Tech Wars (sections/swt-tech-wars.liquid)
   <swt-tech-wars> custom element: drag-handle image compare.
   - Touch / pen: only the handle starts a drag (CSS touch-action: none on the
     handle only), so a thumb on the photo still scrolls the page.
   - Mouse: drag or click anywhere on the photo.
   - Keyboard: the handle is an ARIA slider. Arrows +/-5, Page Up/Down +/-10,
     Home/End to the ends.
   - Optional one-time nudge when the section first comes into view, to show the
     handle moves. Skipped with reduced motion or once the shopper touches it.
   Position lives in the CSS variable --tw-pos on the stage.
   Comparison bars (when the section has them): each spec row carries its two values
   (data-a / data-b). The first number with a unit both values share is compared
   (Hz first for displays, GB/TB normalised, kg -> g). Higher wins, lower wins for
   weight. "Row N bar" settings (data-better) can force a / b / same / none.
   Bars grow from the centre once the table is in view (no animation with
   reduced motion). */
(function () {
  if (customElements.get('swt-tech-wars')) return;

  var MIN = 3;
  var MAX = 97;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(value) {
    return Math.min(MAX, Math.max(MIN, value));
  }

  /* ----- comparison bars: reading the numbers ----- */
  var NUM_RE = /(\d+(?:\.\d+)?)\s*(mah|mp|hz|tb|gb|mb|kg|g|w|x|nits|mm|inch|in|"|\u2033|k)?(?![a-z])/gi;
  var NO_AUTO = { chip: 1, network: 1, sound: 1, star: 1 };

  function readNumbers(text) {
    var out = [];
    if (!text) return out;
    var clean = String(text).replace(/(\d),(?=\d{3}(?!\d))/g, '$1').replace(/(\d),(\d)/g, '$1.$2');
    var match;
    NUM_RE.lastIndex = 0;
    while ((match = NUM_RE.exec(clean)) !== null) {
      var value = parseFloat(match[1]);
      var unit = (match[2] || '').toLowerCase();
      if (unit === '"' || unit === '\u2033' || unit === 'inch') unit = 'in';
      if (unit === 'tb') { value = value * 1024; unit = 'gb'; }
      if (unit === 'mb') { value = value / 1024; unit = 'gb'; }
      if (unit === 'kg') { value = value * 1000; unit = 'g'; }
      if (!isNaN(value)) out.push({ n: value, u: unit });
    }
    return out;
  }

  function pick(list, unit) {
    for (var i = 0; i < list.length; i++) if (list[i].u === unit) return list[i].n;
    return null;
  }

  /* Returns { a, b, lower } or null when the two values cannot be compared */
  function compareValues(icon, textA, textB) {
    var a = readNumbers(textA);
    var b = readNumbers(textB);
    if (!a.length || !b.length) return null;
    var unit = null;
    if (icon === 'display' && pick(a, 'hz') !== null && pick(b, 'hz') !== null) unit = 'hz';
    if (unit === null) {
      for (var i = 0; i < a.length; i++) {
        if (a[i].u !== '' && pick(b, a[i].u) !== null) { unit = a[i].u; break; }
      }
    }
    if (unit === null && icon === 'water' && pick(a, '') !== null && pick(b, '') !== null) unit = '';
    if (unit === null) return null;
    var va = pick(a, unit);
    var vb = pick(b, unit);
    if (va === null || vb === null || va < 0 || vb < 0) return null;
    return { a: va, b: vb, lower: icon === 'weight' || unit === 'g' };
  }

  class SwtTechWars extends HTMLElement {
    connectedCallback() {
      if (this.twBound) return;
      this.stage = this.querySelector('[data-tw-stage]');
      this.knob = this.querySelector('[data-tw-knob]');
      if (!this.stage || !this.knob) return;
      this.twBound = true;

      this.nameA = this.dataset.nameA || 'Product A';
      this.nameB = this.dataset.nameB || 'Product B';
      this.start = clamp(parseFloat(this.dataset.start) || 50);
      this.pos = this.start;
      this.touched = false;
      this.raf = null;

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onKey = this.onKey.bind(this);

      this.knob.addEventListener('pointerdown', this.onDown);
      this.stage.addEventListener('pointerdown', this.onDown);
      this.knob.addEventListener('keydown', this.onKey);

      this.render();
      this.setupHint();
      this.setupBars();
    }

    disconnectedCallback() {
      this.stopDrag();
      if (this.observer) this.observer.disconnect();
      if (this.barObserver) this.barObserver.disconnect();
      if (this.hintRaf) window.cancelAnimationFrame(this.hintRaf);
    }

    /* ----- pointer ----- */
    onDown(event) {
      var onKnob = this.knob.contains(event.target);
      if (event.currentTarget === this.stage && onKnob) return;   /* the knob listener handles it */
      if (!onKnob && event.pointerType !== 'mouse') return;        /* touch on the photo = scroll */
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      event.preventDefault();
      this.cancelHint();
      this.touched = true;
      this.dragging = true;
      this.stage.classList.remove('is-easing');
      this.stage.classList.add('is-dragging');
      if (onKnob && this.knob.focus) this.knob.focus({ preventScroll: true });

      this.rect = this.stage.getBoundingClientRect();
      if (!onKnob) this.setFromX(event.clientX);

      window.addEventListener('pointermove', this.onMove);
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);
    }

    onMove(event) {
      if (!this.dragging) return;
      this.setFromX(event.clientX);
    }

    onUp() {
      this.stopDrag();
    }

    stopDrag() {
      this.dragging = false;
      if (this.stage) this.stage.classList.remove('is-dragging');
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
    }

    setFromX(clientX) {
      var rect = this.rect || this.stage.getBoundingClientRect();
      if (!rect.width) return;
      this.pos = clamp(((clientX - rect.left) / rect.width) * 100);
      this.schedule();
    }

    /* ----- keyboard ----- */
    onKey(event) {
      var step = 0;
      var target = null;
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          step = -5;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          step = 5;
          break;
        case 'PageDown':
          step = -10;
          break;
        case 'PageUp':
          step = 10;
          break;
        case 'Home':
          target = MIN;
          break;
        case 'End':
          target = MAX;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.cancelHint();
      this.touched = true;
      this.pos = clamp(target !== null ? target : this.pos + step);
      if (!reduceMotion) this.stage.classList.add('is-easing');
      this.schedule();
    }

    /* ----- output ----- */
    schedule() {
      if (this.raf) return;
      var self = this;
      this.raf = window.requestAnimationFrame(function () {
        self.raf = null;
        self.render();
      });
    }

    render() {
      var value = Math.round(this.pos * 10) / 10;
      this.stage.style.setProperty('--tw-pos', value + '%');
      var shown = Math.round(this.pos);
      this.knob.setAttribute('aria-valuenow', String(shown));
      this.knob.setAttribute('aria-valuetext', shown + '% ' + this.nameA + ', ' + (100 - shown) + '% ' + this.nameB);
    }

    /* ----- comparison bars ----- */
    setupBars() {
      var panel = this.closest('.swt-tw__panel');
      var table = panel && panel.querySelector('[data-tw-table]');
      if (!table || !table.querySelector('.swt-tw__bar')) return;
      var self = this;
      var rows = Array.prototype.slice.call(table.querySelectorAll('[data-tw-row]'));
      var shown = 0;

      rows.forEach(function (row) {
        var icon = row.dataset.icon || '';
        var better = row.dataset.better || 'auto';
        var verdict = row.querySelector('[data-tw-verdict]');
        var result = better === 'none' ? null : compareValues(icon, row.dataset.a, row.dataset.b);
        if (better === 'auto' && NO_AUTO[icon]) result = null;

        var winner = null;
        var fillA = 1;
        var fillB = 1;
        if (result) {
          var max = Math.max(result.a, result.b);
          var min = Math.min(result.a, result.b);
          if (result.lower) {
            fillA = result.a > 0 ? min / result.a : 1;
            fillB = result.b > 0 ? min / result.b : 1;
          } else if (max > 0) {
            fillA = result.a / max;
            fillB = result.b / max;
          }
          var gap = Math.abs(result.a - result.b) / (max || 1);
          if (gap < 0.005) winner = 'same';
          else if (result.lower) winner = result.a < result.b ? 'a' : 'b';
          else winner = result.a > result.b ? 'a' : 'b';
        }
        if (better === 'a' || better === 'b' || better === 'same') winner = better;

        if (!winner) {
          row.classList.add('no-bar');
          return;
        }
        row.classList.remove('no-bar');
        row.dataset.win = winner;
        row.style.setProperty('--tw-bar-a', Math.max(0.06, fillA).toFixed(3));
        row.style.setProperty('--tw-bar-b', Math.max(0.06, fillB).toFixed(3));
        row.style.setProperty('--tw-bar-delay', (shown * 60) + 'ms');
        shown += 1;
        if (verdict) {
          verdict.textContent = winner === 'same' ? ', even' : ', ' + (winner === 'a' ? self.nameA : self.nameB) + ' is ahead';
        }
      });

      var reveal = function () { table.classList.add('is-bars-shown'); };
      if (reduceMotion || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
        reveal();
        return;
      }
      this.barObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal();
          self.barObserver.disconnect();
          self.barObserver = null;
        });
      }, { threshold: 0.25 });
      this.barObserver.observe(table);
    }

    /* ----- one-time nudge ----- */
    setupHint() {
      if (this.dataset.hint !== 'true' || reduceMotion || !('IntersectionObserver' in window)) return;
      if (window.Shopify && window.Shopify.designMode) return;
      var self = this;
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.observer.disconnect();
          self.observer = null;
          window.setTimeout(function () { self.playHint(); }, 400);
        });
      }, { threshold: 0.6 });
      this.observer.observe(this.stage);
    }

    playHint() {
      if (this.touched) return;
      var self = this;
      var from = this.start;
      var peak = clamp(from + 8);
      var duration = 900;
      var t0 = null;
      var ease = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };
      this.stage.classList.remove('is-easing');
      var tick = function (now) {
        if (self.touched) return;
        if (t0 === null) t0 = now;
        var t = Math.min(1, (now - t0) / duration);
        /* out to the peak and back, no overshoot past either end */
        var phase = t < 0.5 ? ease(t * 2) : ease((1 - t) * 2);
        self.pos = from + (peak - from) * phase;
        self.render();
        if (t < 1) {
          self.hintRaf = window.requestAnimationFrame(tick);
        } else {
          self.pos = from;
          self.render();
          self.hintRaf = null;
        }
      };
      this.hintRaf = window.requestAnimationFrame(tick);
    }

    cancelHint() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.hintRaf) {
        window.cancelAnimationFrame(this.hintRaf);
        this.hintRaf = null;
      }
    }
  }

  customElements.define('swt-tech-wars', SwtTechWars);
})();
